import React from 'react';
import { Sparkles } from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import type { AiModelOption } from '../../../configuration-settings';
import EmailAiConversation from '../EmailAiConversation';
import { useEmailAiAssistant } from '../../hooks/useEmailAiAssistant';
import type { EmailAiAssistantProps } from './emailAiAssistant.types';
import { EmailAiAssistantShell } from './EmailAiAssistantShell';

export const EmailAiAssistant: React.FC<EmailAiAssistantProps> = ({
  open,
  onClose,
  context = 'compose',
  presentation = 'overlay',
  compact = false,
  subject,
  body,
  getBody,
  selectedText,
  getSelectedText,
  onReplaceBody,
  onInsertAtCursor,
  onBumpEditorKey,
  onCreateReply,
  emailId,
  partitionAt,
  clientId,
  composeSessionId,
}) => {
  const { colors } = useTheme();
  const controller = useEmailAiAssistant({
    open,
    context,
    subject,
    body,
    getBody,
    selectedText,
    getSelectedText,
    onClose,
    onReplaceBody,
    onInsertAtCursor,
    onBumpEditorKey,
    emailId,
    partitionAt,
    clientId,
    composeSessionId,
  });

  const {
    aiEmailsEnabled,
    isRead,
    canEditDraft,
    openInAiChatHref,
    recordingNote,
    prompts,
    models,
    promptsError,
    drawerLoadingMeta,
    loadingHistory,
    loading,
    runningPromptName,
    cancelInFlight,
    selectedModelId,
    setSelectedModelId,
    selectedPromptId,
    setSelectedPromptId,
    needAdditional,
    needSelected,
    canRun,
    additionalText,
    setAdditionalText,
    selectedPromptCode,
    thread,
    chatInput,
    setChatInput,
    handleRunServerAi,
    handleSendMessage,
    clearThread,
    handleInsertAtCursor,
    handleReplaceBody,
    selectedLen,
    showSmartReplies,
  } = controller;

  const useCompactConversation = presentation === 'inline' && compact;

  const disabledPanel = (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
      <Sparkles className={cn('h-8 w-8', colors.textMuted)} aria-hidden />
      <p className={cn('text-sm font-medium', colors.text)}>AI email assistance is off</p>
      <p className={cn('max-w-xs text-xs leading-relaxed', colors.textMuted)}>
        An administrator can enable it in Settings → Application → AI email assistance.
      </p>
    </div>
  );

  const panelBody = !aiEmailsEnabled ? (
    disabledPanel
  ) : (
    <div className="flex min-h-0 flex-1 flex-col">
      {recordingNote ? (
        <p className={cn('shrink-0 border-b px-3 py-2 text-[11px] leading-snug', colors.border, colors.textMuted)}>
          {recordingNote}
        </p>
      ) : null}

      {promptsError ? (
        <div className={cn('m-3 rounded-xl border px-3 py-2.5 text-sm', colors.border, colors.error, 'bg-red-500/5')}>
          {promptsError}
        </div>
      ) : drawerLoadingMeta && prompts.length === 0 ? (
        <div className={cn('flex flex-1 items-center justify-center p-6 text-sm', colors.textMuted)}>
          Loading AI prompts…
        </div>
      ) : loadingHistory && thread.length === 0 ? (
        <div className={cn('flex flex-1 items-center justify-center p-6 text-sm', colors.textMuted)}>
          Restoring conversation…
        </div>
      ) : (
        <EmailAiConversation
          compact={useCompactConversation}
          loading={loading}
          runningPromptName={runningPromptName}
          onCancelLoading={loading ? cancelInFlight : undefined}
          prompts={prompts}
          models={models as AiModelOption[]}
          selectedModelId={selectedModelId}
          onModelChange={setSelectedModelId}
          selectedPromptId={selectedPromptId}
          onSelectPrompt={setSelectedPromptId}
          needAdditional={needAdditional}
          needSelected={needSelected}
          canRunPrompt={canRun}
          additionalText={additionalText}
          onAdditionalTextChange={setAdditionalText}
          selectedPromptCode={selectedPromptCode}
          thread={thread}
          chatInput={chatInput}
          onChatInputChange={setChatInput}
          onRunPrompt={handleRunServerAi}
          onSendMessage={handleSendMessage}
          onClearThread={clearThread}
          canEditDraft={canEditDraft}
          onInsertAtCursor={onInsertAtCursor ? handleInsertAtCursor : undefined}
          onReplaceBody={onReplaceBody ? handleReplaceBody : undefined}
          onCreateReply={onCreateReply}
          isRead={isRead}
          selectedLen={selectedLen}
          emailId={showSmartReplies ? emailId : undefined}
          partitionAt={partitionAt}
          clientId={clientId}
        />
      )}
    </div>
  );

  return (
    <EmailAiAssistantShell
      open={open}
      onClose={onClose}
      presentation={presentation}
      compact={compact}
      isRead={isRead}
      loading={loading}
      openInAiChatHref={openInAiChatHref}
    >
      {panelBody}
    </EmailAiAssistantShell>
  );
};

export default EmailAiAssistant;

export type { EmailAiAssistantProps, EmailAiPromptMeta } from './emailAiAssistant.types';
